import React, { memo, useCallback, useContext } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, func } from 'prop-types'

import useRequireLogin from 'Pods/ThreeDStudio/Hooks/useRequireLogin'
import useImageUploader from 'Pods/ThreeDStudio/Shared/ActionSelector/Hooks/useImageUploader'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { openImageEditor } from 'Pods/ThreeDStudio/Store/actions/ui'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import IdeationActions from './Components/IdeationActions/IdeationActions'
import LoadingState from './Components/LoadingState/LoadingState'
import PasteUrlArea from './Components/PasteUrlArea/PasteUrlArea'
import PreviewWrap from './Components/PreviewWrap/PreviewWrap'
import UploadPrompt from './Components/UploadPrompt/UploadPrompt'

function ImageUploader({ sliceName, storeSchemaKey, setInputImage }) {
  const inputImage = useThreeDStudioStore(state => state[sliceName].inputImage)
  const inputImageUrl = useThreeDStudioStore(state => state[sliceName].inputImageUrl)
  const { ideationFlowEnabled, extractingCoreStyles } = useContext(ThreeDStudioPageContext)
  const requireLogin = useRequireLogin()
  // The ideation entry points belong only to the Generate 3D Model panel, not the
  // shared retexture (textureSettings) uploader. `ideationFlowEnabled` is server-rendered
  // from the actorless flag and refined by pageConfig, so the row is in the SSR HTML for
  // every visitor - signed-out included, where the buttons prompt registration. Also render
  // it during style extraction so its CSS reaches the critical bundle.
  const showIdeationActions = (ideationFlowEnabled || extractingCoreStyles) && sliceName === 'modelSettings'
  // Clicking the preview is the same hand-off as the ideation row's "Edit image" button, so it
  // belongs wherever that row does - minus `extractingCoreStyles`, which only forces the row into
  // the critical-CSS pass and must not make the image clickable during extraction.
  const canEditPreview = ideationFlowEnabled && sliceName === 'modelSettings'

  const {
    inputRef,
    allowedTypes,
    isLoading,
    onDragOver,
    onDrop,
    onFileChange,
    onFileClick,
    handleRemovePreview,
    handlePasteUrlUpload,
  } = useImageUploader(setInputImage, storeSchemaKey, sliceName)

  const canAcceptFile = !isLoading
  const showUploadPrompt = !inputImageUrl && !isLoading

  const handleBrowseClick = useCallback(event => {
    event.preventDefault()
    event.stopPropagation()
    onFileClick()
  }, [onFileClick])

  const handleEditPreview = useCallback(() => {
    if (!requireLogin()) return

    openImageEditor({ id: inputImage, url: inputImageUrl })
  }, [requireLogin, inputImage, inputImageUrl])

  return (
    <>
      <div
        className={classnames(
          'image-uploader',
          inputImageUrl && 'image-uploader--has-preview',
          isLoading && 'image-uploader--loading',
        )}
        onDragOver={onDragOver}
        onDrop={canAcceptFile ? onDrop : undefined}
        onClick={showUploadPrompt ? onFileClick : undefined}
        role="region"
        aria-label="Image upload"
        aria-busy={isLoading}
      >
        <input
          ref={inputRef}
          type="file"
          accept={allowedTypes.join(',')}
          onChange={canAcceptFile ? onFileChange : undefined}
          className="image-uploader__input"
          aria-hidden
          tabIndex={-1}
          disabled={!canAcceptFile}
        />
        <div className="image-uploader__top">
          {isLoading && <LoadingState />}
          {inputImageUrl && !isLoading && (
            <PreviewWrap
              imageUrl={inputImageUrl}
              onRemovePreview={handleRemovePreview}
              onEdit={canEditPreview ? handleEditPreview : undefined}
            />
          )}
          {showUploadPrompt && <UploadPrompt onBrowseClick={handleBrowseClick} />}
        </div>
      </div>
      <PasteUrlArea
        isLoading={isLoading}
        onPasteUrlUpload={handlePasteUrlUpload}
      />
      {showIdeationActions && <IdeationActions />}
    </>
  )
}

ImageUploader.propTypes = {
  sliceName: string.isRequired,
  storeSchemaKey: string.isRequired,
  setInputImage: func.isRequired,
}

export default memo(ImageUploader)
